import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DATA_SET_PAGINATION_COUNT, SEARCH_PARAMS } from 'constants/dataSets';
import { STATUS } from 'constants/formFields';
import { MODALS_IRO_IDENTIFICATION, REQUEST_STATUSES } from 'constants/general';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import useTableCheckboxForm from 'pages/MaterialAssessmentPages/MaterialAssessmentIROIndetificator/hooks/useTableCheckboxForm';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import {
  useApproveRequestTemplatesMutation, useDeleteTemplateMutation,
  useGetDefaultTemplatesListQuery, useGetIRORequestListQuery,
  useRejectRequestTemplatesMutation,
  useUpdateTemplateMutation,
} from 'store/slices/iroStandardsAdmin/apis/iroStandardsAdminApi';
import { useGetStandardsListQuery } from 'store/slices/standards/apis/standardsApi';

import StandardsIROView from './StandardsIROView';

import type { ISearchInputHandler } from 'components/TextField/interfaces/ITextField';
import type IRequestItem from 'store/slices/iroStandardsAdmin/interfaces/IRequestItem';
import type IItemIROCustom from 'store/slices/materialAssessment/interfaces/IItemIROCustom';
import type ITemplateItem from 'store/slices/materialAssessment/interfaces/ITemplateItem';

function StandardsIRO() {
  const [activeTab, setActiveTab] = useState(1);
  const [offset, setOffset] = useState(0);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(-1);
  const [selectEditItem, setSelectEditItem] = useState<ITemplateItem | null>(null);
  const [items, setItems] = useState<ITemplateItem[]>([]);
  const [openModal, setOpenModal] = useState('');

  const [approveRequestTemplates] = useApproveRequestTemplatesMutation();
  const [rejectRequestTemplates] = useRejectRequestTemplatesMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [updateTemplate] = useUpdateTemplateMutation();

  const {
    control,
    watch,
    getValues,
  } = useTableCheckboxForm();

  const categoryFilter = watch(STATUS);

  const { data: standards, isFetching: isStandardsLoading } = useGetStandardsListQuery({
    offset: 0,
  });

  const { data: naceCodeTreeData = { data: {} }, isLoading: isGetNaceCodeTreeRecords } = useGetNaceCodeTreeQuery();
  const { data: templates, isFetching: isLoadingTemplates } = useGetDefaultTemplatesListQuery({
    offset,
    categories: categoryFilter?.filter((item): item is string => item !== undefined),
    description: searchParams.get(SEARCH_PARAMS.description) || '',
  });

  const { data: templatesRequests, isFetching: isLoadingTemplatesRequests } = useGetIRORequestListQuery({
    offset,
    status: REQUEST_STATUSES.PENDING,
  });

  const hasMore = activeTab === 1 ? templates?.data?.meta?.hasNext : templatesRequests?.data?.meta?.hasNext;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const createTemplateItemBase = (idRequest: number): ITemplateItem => ({
    idRequest,
    id: 0,
    description: '',
    naceSection: '',
    naceCode: '',
    standard: { id: 0, name: '' },
    topic: { id: 0, title: '', depth: 0 },
    impactType: '',
    valueChainSection: '',
    category: '',
    impact: '',
  });

  const mapIroDataToTemplateItem = (
    iroData: any,
    baseItem: ITemplateItem,
  ): ITemplateItem => ({
    ...baseItem,
    id: iroData.id,
    description: iroData.description,
    naceSection: iroData.naceSection,
    naceCode: iroData.naceCode,
    standard: iroData.standard,
    topic: iroData.topic,
    impactType: iroData.impactType,
    valueChainSection: iroData.valueChainSection,
    category: iroData.category,
    impact: iroData.impact,
  });

  const handleFormattedItems = useCallback((itemsReq: IRequestItem[]): ITemplateItem[] => itemsReq.map((item) => {
    const baseItem = createTemplateItemBase(item.id);

    const iroData = item.maIroImpact || item.maIroOpportunity || item.maIroRisk;

    return iroData ? mapIroDataToTemplateItem(iroData, baseItem) : baseItem;
  }), []);

  useEffect(() => {
    if (activeTab === 1) {
      if (templates?.data.items) {
        if (offset === 0) {
          setItems(templates.data.items);
        } else {
          setItems((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = templates.data.items.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }

    if (activeTab === 2) {
      if (templatesRequests?.data.items) {
        const formattedItems = handleFormattedItems(templatesRequests?.data?.items);
        if (offset === 0) {
          setItems(formattedItems);
        } else {
          setItems((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = formattedItems.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }
  }, [templatesRequests, offset, activeTab, templates, handleFormattedItems]);

  const handleClickTab = (tabId: number) => {
    setActiveTab(tabId);
    setOffset(0);
  };

  const handleCloseModal = () => {
    setSelectedItem(-1);
    setDeleteModalOpen(false);
    setSelectEditItem(null);
    setOpenModal('');
  };

  const handleDeleteTemplate = (id: number) => {
    setSelectedItem(id);
    setDeleteModalOpen(true);
  };

  const handleConfirm = () => {
    if (selectedItem !== -1) {
      deleteTemplate({ id: selectedItem });
      setSelectedItem(-1);
      setItems((prevItems) => prevItems.filter((item) => item.id !== selectedItem));
    }
    handleCloseModal();
  };

  const handleEditCustomItem = () => {
    if (activeTab === 1) {
      setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_IMPACT);
    }
  };

  const handleEdit = (id: number) => {
    const editItem = items.find((item) => item.id === id) || null;
    if (editItem) {
      setSelectEditItem(editItem);
      handleEditCustomItem();
    }
  };

  const handleApprove = (idRequest: number) => {
    approveRequestTemplates({ id: idRequest });
  };

  const handleReject = (idRequest: number) => {
    rejectRequestTemplates({ id: idRequest });
  };

  const updateIROItem = (data: IItemIROCustom) => {
    updateTemplate({
      data,
      id: selectEditItem?.id,
    }).then(() => {
      setOffset(0);
    });
  };

  const debouncedSearch = useDebouncedCallback(() => {
    const { search } = getValues();
    const newSearchParams = {
      ...Object.fromEntries(searchParams),
      [SEARCH_PARAMS.description]: search || '',
      [SEARCH_PARAMS.offset]: '0',
    };
    setSearchParams(newSearchParams, { replace: true });
  });

  const onSearchChange = ({ search, onChange }: ISearchInputHandler) => {
    onChange(search);
    debouncedSearch();
  };

  const loading = isLoadingTemplates || isGetNaceCodeTreeRecords || isLoadingTemplatesRequests
  || isStandardsLoading;
  return (
    <StandardsIROView
      loading={loading}
      activeTab={activeTab}
      handleClickTab={handleClickTab}
      items={items}
      handleDeleteTemplate={handleDeleteTemplate}
      isDeleteModalOpen={isDeleteModalOpen}
      handleDeleteConfirm={handleConfirm}
      handleCloseModal={handleCloseModal}
      hasMore={hasMore || false}
      onLoadMore={onLoadMore}
      handleEdit={handleEdit}
      selectEditItem={selectEditItem}
      openModal={openModal}
      naceCodeTreeData={naceCodeTreeData}
      handleApprove={handleApprove}
      handleReject={handleReject}
      standards={standards?.data.items || []}
      updateIROItem={updateIROItem}
      control={control}
      setOffset={setOffset}
      onSearchChange={onSearchChange}
      searchValue={searchParams.get(SEARCH_PARAMS.description) || ''}
    />
  );
}

export default StandardsIRO;
